import { useDependencies } from "../useDependencies";
import { useAuth } from "../useAuth";
import { useDocumentationArea } from "../useDocumentationArea";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { useDocumentationAreaMutation } from "../useDocumentationAreaMutation";
import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { Actor } from "src/contexts/documentationArea/domain/Actor";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { OrderIdentityVerification } from "src/contexts/documentationArea/domain/OrderIdentityVerification";

interface MutationFnProps {
  identityVerificationId: string;
  documentType: IdentityVerificationDocumentTypes;
}

export const useSelectIdentityVerificationDocumentType = () => {
  const { token } = useAuth();
  const { documentationArea } = useDocumentationArea();
  const { documentationAreaController } = useDependencies();

  if (!documentationArea) throw new DocumentationAreaNotFoundError();

  const optimisticUpdate = (
    { identityVerificationId, documentType }: MutationFnProps,
    documentationArea: DocumentationArea,
  ) => {
    // works with one actor
    const actor = documentationArea.getActor();
    const identityVerification = actor
      .getIdentityVerificationOrders()
      .find((idv) => idv.getId().toString() === identityVerificationId);

    if (!identityVerification) throw new Error("Identity verification to update not found");
    return new DocumentationArea({
      id: documentationArea.getId(),
      type: documentationArea.getType(),
      actor: new Actor({
        id: actor.getId(),
        identity: actor.getIdentity(),
        signedAcceptanceIds: actor.getSignedAcceptanceIds(),
        notifications: actor.getNotifications(),
        orders: [
          ...actor
            .getOrders()
            .filter((order) => order.getId().toString() !== identityVerificationId),
          new OrderIdentityVerification({
            id: identityVerification.getId(),
            request: {
              type: identityVerification.getRequest().type,
              data: {
                ...identityVerification.getRequest().data,
                stagedState: {
                  ...identityVerification.getRequest().data.stagedState,
                  documentType: documentType,
                  country: null,
                },
              },
            },
            responses: [],
            retrievalMethods: identityVerification.getRetrievalMethods(),
            status: identityVerification.getStatus(),
          }),
        ],
        prefilledData: actor.getPrefilledData(),
      }),
      availableActors: documentationArea.getAvailableActors(),
      locale: documentationArea.getLocale(),
    });
  };

  const mutationFn = async ({ identityVerificationId, documentType }: MutationFnProps) => {
    return await documentationAreaController.patchActorIDVOrder({
      accessToken: token,
      documentationAreaId: documentationArea.getId(),
      actorId: documentationArea.getActor().getId(),
      orderId: identityVerificationId,
      documentType,
    });
  };

  const { mutate, isSuccess, isError } = useDocumentationAreaMutation({
    mutationFn,
    optimisticUpdate,
  });

  return {
    mutate,
    isSuccess,
    isError,
  };
};
